import {
  Box,
  Divider,
  FlexBox,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { useFormContext, useWatch } from 'react-hook-form';

import { useCustomForms } from 'app/views/layout/views/settings/views/customForms/hooks/useCustomForms/useCustomForms';
import { HookFormSelectionListSingle } from 'components/hookForm';

import { FormValues, messengerSettingsFormFields } from '../../types';

import { EndpointSelector } from './endpointSelector';
import { FormSelector } from './formSelector';
import { useFormEndpoints } from './useFormEndpoints';

type MessengerModeOption = {
  id: FormValues['messengerMode'];
  name: string;
};

const options: MessengerModeOption[] = [
  {
    id: 'default-widget-mode',
    name: 'Allow to start a live conversation (default)',
  },
  {
    id: 'form-without-conversation-widget-mode',
    name: 'Only allows contact forms',
  },
];

type Props = {
  currentMessengerMode?: FormValues['messengerMode'];
  formOnlyModeContactFormId?: string;
};

export function MessengerMode({ currentMessengerMode }: Props): JSX.Element {
  const { forms } = useCustomForms();

  const {
    formState: { errors },
    setValue,
  } = useFormContext<FormValues>();

  const isFormOnlyMode =
    currentMessengerMode === 'form-without-conversation-widget-mode';

  const selectedFormId = useWatch<FormValues, 'formOnlyModeContactFormId'>({
    name: 'formOnlyModeContactFormId',
  });

  const endpoints = useFormEndpoints(selectedFormId);

  const selectedFormIsValid = errors.formOnlyModeContactFormId === undefined;

  function resetFormIdField(value: FormValues['messengerMode'] | undefined) {
    if (value === 'default-widget-mode') {
      setValue(
        messengerSettingsFormFields.formOnlyModeContactFormId,
        undefined,
      );
      setValue(messengerSettingsFormFields.formOnlyModeEndpointId, undefined);
    }
  }

  return (
    <FlexBox flexDirection="column" padding="400 0">
      <Text.Heading margin="300 0">Initial state</Text.Heading>

      <HookFormSelectionListSingle
        name={messengerSettingsFormFields.messengerMode}
        label="Messenger initial state"
        componentProps={{
          onChange: (event) =>
            resetFormIdField(
              event.selectedItem?.value as
                | FormValues['messengerMode']
                | undefined,
            ),
          getItemTitleByValue(itemValue) {
            return options.find((option) => option.id === itemValue)?.name;
          },
        }}
      >
        {options.map((option) => (
          <SelectionList.Item key={option.id} value={option.id}>
            {option.name}
          </SelectionList.Item>
        ))}
      </HookFormSelectionListSingle>

      {isFormOnlyMode && (
        <FlexBox flexDirection="column" gap="300">
          <Text.Paragraph variant="2" subdued>
            The messenger will launch a form instead of opening a conversation
            when the “Start a Conversation” button is clicked.
          </Text.Paragraph>
          <Divider />
          <Text.Paragraph variant="2" subdued>
            To be able to use this functionality you need to select a form that
            has an email field and a webform endpoint created
          </Text.Paragraph>
          <Box>
            <FormSelector forms={forms} />
            <EndpointSelector
              endpoints={endpoints}
              disabled={!selectedFormIsValid}
            />
          </Box>
        </FlexBox>
      )}
    </FlexBox>
  );
}
